import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl, { LngLat, Map } from 'mapbox-gl';
import { mapboxConfig } from 'src/config/mapboxConfig';
import { useAmplitudeWebsite } from 'src/hooks/useAmplitudeWebsite';
import { MapMarker } from 'src/components/MapMarker';
import Store from 'src/api/public/Store';

import 'mapbox-gl/dist/mapbox-gl.css';
import style from './style.module.scss';

interface ControllerProps extends React.HTMLAttributes<HTMLDivElement> {
    stores: Store[];
    map: Map | null;
    setMap: React.Dispatch<React.SetStateAction<Map | null>>;
    setList: React.Dispatch<React.SetStateAction<Store[]>> | null;
    getClosestStore: GetClosestStoreType;
    setSelectedStore: React.Dispatch<React.SetStateAction<Store | null>>;
    selectedStore: Store | null;
    growmobile: boolean;
}

interface MarkerStore {
    id: string;
    element: Store;
}

export type GetClosestStoreType = (coord: LngLat, select: boolean) => { element: Store; id?: string; dist?: number };

export const Controller: React.FC<ControllerProps> = ({
    stores,
    map,
    setMap,
    setList,
    getClosestStore,
    setSelectedStore,
    selectedStore,
    growmobile,
    ...props
}) => {
    const { isEasyVO } = useAmplitudeWebsite();
    const mapRef = useRef<HTMLDivElement | null>(null);
    const classNames = style.controller + (props.className ? ' ' + props.className : '');
    const [markers, setMarkers] = useState<MarkerStore[]>([]);
    const [selected, setSelected] = useState<string>('');
    const [listening, setListening] = useState<boolean>(false);
    const [byClick, setByClick] = useState<boolean>(false);
    mapboxgl.accessToken = mapboxConfig.accessToken ?? '';

    const isInRectangle = useCallback((bounds, element) => {
        return (
            element.longitude < bounds._ne.lng &&
            element.longitude > bounds._sw.lng &&
            element.latitude < bounds._ne.lat &&
            element.latitude > bounds._sw.lat
        );
    }, []);

    const getDistance = useCallback((a, b) => {
        let coordA = new LngLat(a.longitude, a.latitude);
        let coordB = new LngLat(b.longitude, b.latitude);
        return coordA.distanceTo(coordB);
    }, []);

    const compareName = useCallback((a, b) => {
        return a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
    }, []);

    const getFitBounds = useCallback(() => {
        let minLat = stores[0].latitude;
        let maxLat = stores[0].latitude;
        let minLon = stores[0].longitude;
        let maxLon = stores[0].longitude;
        for (let i = 0; i < stores.length; i++) {
            if (stores[i].latitude < minLat) {
                minLat = stores[i].latitude;
            }
            if (stores[i].latitude > maxLat) {
                maxLat = stores[i].latitude;
            }
            if (stores[i].longitude < minLon) {
                minLon = stores[i].longitude;
            }
            if (stores[i].longitude > maxLon) {
                maxLon = stores[i].longitude;
            }
        }
        return {
            minLon: minLon,
            minLat: minLat,
            maxLon: maxLon,
            maxLat: maxLat,
        };
    }, [stores]);

    const initialize = useCallback(() => {
        if (map?.loaded()) {
            map.resize();
            const bounds = getFitBounds();
            map.fitBounds(
                [
                    [bounds.minLon, bounds.minLat],
                    [bounds.maxLon, bounds.maxLat],
                ],
                { padding: 40 },
            );
        }
    }, [getFitBounds, map]);

    const actualizeList = useCallback(() => {
        if (!setList) return;
        let newList = [...markers]
            .filter((e) => {
                return (
                    isInRectangle(map?.getBounds(), e.element) &&
                    e.id !== selected &&
                    (selectedStore === null ||
                        (e.element.longitude !== selectedStore.longitude &&
                            e.element.latitude !== selectedStore.latitude))
                );
            })
            .map((e) => e.element)
            .sort(selected === '' ? compareName : getDistance);
        setList(newList);
    }, [setList, markers, selected, isInRectangle, map, getDistance, compareName, selectedStore]);

    const clickListener = useCallback(
        (e) => {
            const selectedMarker = document.getElementsByClassName('selectedMarker');
            while (selectedMarker.length > 0) {
                selectedMarker[0].classList.remove('selectedMarker');
            }
            e.currentTarget.classList.add('selectedMarker');
            setSelected(e.currentTarget.id);
            setByClick(true);
        },
        [setSelected],
    );

    useEffect(() => {
        if (selected) {
            const markersList = document.querySelectorAll('.mapboxgl-marker');
            const marker = document.getElementById(selected);
            Array.from(markersList).map((el) => el.classList.remove('selectedMarker'));
            marker?.classList.add('selectedMarker');
            initialize();
        }
    }, [selected, initialize]);

    // list actualizer on move and scroll
    useEffect(() => {
        const actualize = () => actualizeList();

        if (map !== null && listening === false) {
            setListening(true);
            map.on('load', actualize);
            map.on('load', initialize);
            map.on('zoomend', actualize);
            map.on('move', actualize);
        }
    }, [map, actualizeList, listening, setListening, initialize]);

    //list and map actualizer if modified by location search or geolocalization
    useEffect(() => {
        if (selectedStore !== null) {
            let selectedMarker = markers.find(
                (e) => e.element.latitude === selectedStore.latitude && e.element.longitude === selectedStore.longitude,
            );
            if (selectedMarker && selectedMarker.id) {
                setSelected(selectedMarker.id);
            }
        }
    }, [setSelected, selectedStore, markers]);

    //list actualizer on select
    useEffect(() => {
        //unselect old one
        let list = document.getElementsByClassName(style.selectedMarker);
        for (let i = 0; i < list.length; i++) {
            list[i].classList.add(style.marker);
            list[i].classList.remove(style.selectedMarker);
        }

        //then set the selected one as selected
        if (selected !== '') {
            document.getElementById(selected)?.classList.add(style.selectedMarker);
            document.getElementById(selected)?.classList.remove(style.marker);
        }
        let store = markers.find((e) => e.id === selected);

        if (byClick && store) {
            setSelectedStore(store.element);
            setByClick(false);
            actualizeList();
        }
    }, [selected, setSelectedStore, markers, actualizeList, byClick, setByClick]);

    //map initializer
    useEffect(() => {
        if (!mapRef.current || map || !stores.length) return;
        const theMap = new Map({
            ...mapboxConfig,
            style: 'mapbox://styles/smartfire/ckv8c8ndx9fgf14o3prmqwudr',
            container: mapRef.current,
        });
        setMap(theMap);
        setListening(false);

        let storeDatas = [];
        //and create all markers
        for (let i = 0; i < stores.length; i++) {
            const markerId = 'map-marker-' + i;
            const el = MapMarker(isEasyVO, markerId);
            new mapboxgl.Marker({ element: el, anchor: 'bottom' })
                .setLngLat([stores[i].longitude, stores[i].latitude])
                .addTo(theMap);
            el.addEventListener('click', clickListener);
            storeDatas.push({ id: markerId, element: stores[i] });
        }
        setMarkers(storeDatas);
    }, [isEasyVO, mapRef, stores, setMap, isInRectangle, setSelected, setMarkers, getClosestStore, clickListener, map]);

    return (
        <div {...props} className={classNames}>
            <div className={growmobile ? style.growingMapContainer : style.mapContainer} ref={mapRef} />
        </div>
    );
};
